import { useSessionStore } from "@auth/sessionStore";
import {
  pimsActivityClient,
  type PimsHistoryFilters,
} from "@pims/pimsActivityClient";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";

export const pimsActivityKeys = {
  /** Prefix for invalidating every PIMS activity query. */
  all: ["pimsActivity"] as const,
  detail: (clinicUuid: string) => ["pimsActivity", clinicUuid] as const,
  history: (clinicUuid: string, filters: PimsHistoryFilters = {}) =>
    ["pimsActivity", clinicUuid, "history", filters] as const,
};

/**
 * The clinic's live PIMS activity. Static per load (no polling yet — see
 * WEB-3223); retrigger by invalidating {@link pimsActivityKeys.all}.
 */
export const usePimsActivity = () => {
  const clinicUuid = useSessionStore((s) => s.clinic?.clinicUuid ?? "");
  return useSuspenseQuery({
    queryKey: pimsActivityKeys.detail(clinicUuid),
    queryFn: () => pimsActivityClient.get(),
  });
};

/**
 * The clinic's most recent settled PIMS work (one page, newest-first), narrowed
 * by the audit filters (WEB-3250). The filters are part of the query key, so
 * changing a filter refetches the matching page.
 */
export const usePimsHistory = (filters: PimsHistoryFilters = {}) => {
  const clinicUuid = useSessionStore((s) => s.clinic?.clinicUuid ?? "");
  return useSuspenseQuery({
    queryKey: pimsActivityKeys.history(clinicUuid, filters),
    queryFn: () => pimsActivityClient.history(filters),
  });
};

/**
 * Cancel a queued/pending PIMS task (WEB-3227). Invalidates on every outcome:
 * success drops the item (it reappears in history as `cancelled`), and a lost
 * race (409/404) refetches the stale list. Both races are expected, not
 * failures, so the refetched list is the only feedback — the item stays put.
 */
export const useCancelPimsTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pimsActivityClient.cancel,
    meta: { action: "cancel the task" },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: pimsActivityKeys.all }),
  });
};

/**
 * Re-run a failed/timed-out PIMS task (WEB-3270). Invalidating on every outcome
 * is what retires the source's Retry button, since the refetched clone carries
 * the `retryOf` link the list reads.
 */
export const useRetryPimsTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pimsActivityClient.retry,
    meta: { action: "retry the task" },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: pimsActivityKeys.all }),
  });
};
